// packages
import classNames from "classnames";
import { useCallback, useContext, useState, useMemo, useEffect } from "react";
import { AlertColor } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

// components
import BrokerInviteModal from "../../../components/contract/brokerInviteModal";
import EscalationStatusBanner from "./EscalationStatusBanner";
import PrimaryButton from "../../../components/common/PrimaryButton/PrimaryButton";
import Modal from "../../../components/common/Modal";
import CustomizedSnackbars from "../../../components/customizedSnackbars";

import { IconButton } from "@mui/material";
import BackButton from "../../../components/contract/backButton";
import { User } from "../../../components/common/user";
import Gallery from "../../../components/contract/gallery";
import AdditionalInformation from "../contractDetails/addittionalDetails";
import { MainDetailsHeading } from "../contractDetails/mainDetails/MainDetailsHeadding";

// context providers
import ContractActions from "../actions";
import MoreIcon from "@mui/icons-material/MoreVert";

// types
import ContractType from "../contractType";
import { ECONTRACT_STATUS } from "../../../enums";

// styles
import styles from "./styles.module.scss";

// context and providers
import { ContractContext } from "../../../providers/contract";

import React from "react";
import { PAYMENT_DETAILS, PAYMENT_PROOF_ATTACHMENT } from "./constants";
import { getBankTransferDetails } from "../../../setup/api/services/payment";
import moment from "moment";
import useLocalStorage from "../../../hooks/useLocalStorage";
import {
  EContractType,
  ETrasactionsType,
  UserProfile,
} from "../../../setup/api/services/contract/models";

export interface IRightSection {
  backHandler: (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | undefined
  ) => void;
  feedBack: () => void;
}

const RightSection: React.FC<IRightSection> = ({
  backHandler,
  feedBack,
}: IRightSection): JSX.Element => {
  const { contract, contractType } = useContext(ContractContext);
  const [displayDetails, setDisplayDetails] = useState(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string | undefined>(undefined);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [brokerModal, setBrokerModal] = useState<boolean>(false);
  const [inviteType, setInviteType] = useState<"seller" | "buyer">();

  // menu options
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);
  const [images, setImages] = useState<string[]>([]);

  const isMenuOpen = Boolean(mobileMoreAnchorEl);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userId] = useLocalStorage("userId", null);
  const brokerId = useMemo(() => {
    return String(contract?.partiesProfiles?.BROKER?.[0]?.id);
  }, [contract?.partiesProfiles?.BROKER]);

  const closeBrokerModal = useCallback(() => {
    setBrokerModal(false);
  }, []);
  // alerts
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>();
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  const onClose = () => {
    setShowModal(false);
    window.location.reload();
  };

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const onCancel = useCallback(() => {
    setSnackBarTitle("");
    setSnackBarColor(undefined);
    setSnackBarOpen(false);
  }, []);

  const inviteUser = useCallback((type) => {
    setInviteType(type);
    setBrokerModal(true);
  }, []);

  const handleMobileMenuClose = useCallback(() => {
    setMobileMoreAnchorEl(null);
  }, []);

  const handleMobileMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setMobileMoreAnchorEl(event.currentTarget);
    },
    []
  );

  const renderUserActions = useCallback(
    (title: string, phoneNumber: string, userAction?: () => void) => {
      return (
        <div className={styles["no_owner"]}>
          <User
            name={title}
            phoneNumber={phoneNumber}
            userAction={brokerId == userId ? userAction : undefined}
            userActionText={t("lbl_invite")}
          />
        </div>
      );
    },
    [brokerId, t, userId]
  );

  const renderUserDetails = useCallback(
    (user: Partial<UserProfile>, type: "BROKER" | "CUSTOMER" | "PROVIDER") => {
      return (
        <div className={styles["owner"]}>
          <User
            imageUrl={user.profile?.photoUrl}
            name={
              type === "BROKER"
                ? t("lbl_broker")
                : type === "CUSTOMER"
                ? t("lbl_depositor")
                : t("lbl_receiver")
            }
            phoneNumber={user.profile?.firstName || ""}
            nationalIdVerified={user.nationalIdVerified}
          />
        </div>
      );
    },
    [t]
  );
  const checkCashoutTransaction = useCallback(() => {
    return contract?.transactions?.some?.(
      (transaction) => transaction?.type === ETrasactionsType.CASH_OUT
    );
  }, [contract?.transactions]);

  const renderActions = useCallback(() => {
    return [
      ECONTRACT_STATUS.DELIVERY_ESCALATION,
      ECONTRACT_STATUS.INSPECTION_ESCALATION,
      ECONTRACT_STATUS.CREATED,
      ECONTRACT_STATUS.SUBMITTED,
      ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_RESOLVE,
      ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_CLOSE,
      ECONTRACT_STATUS.WAITING_FOR_DELIVERY,
      ECONTRACT_STATUS.ITEM_INSPECTION,
    ].includes(contract?.status as ECONTRACT_STATUS);
  }, [contract?.status]);

  //
  const getBankTransfer = useCallback(async () => {
    const referenceTransactionId = contract?.transactions
      ? contract?.transactions[0]?.referenceTransactionId
      : undefined;

    if (referenceTransactionId) {
      try {
        const data = await getBankTransferDetails(referenceTransactionId);
        if (data?.data?.docs) setImages(data?.data?.docs);
      } catch (error) {
        console.error("error", error);
      }
    }
  }, [contract?.transactions]);

  useEffect(() => {
    if (contract?.status === ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION) {
      getBankTransfer();
    }
  }, [contract, contract?.status, getBankTransfer]);

  const contractDetails = useCallback(() => {
    const broker = contract?.partiesProfiles?.BROKER?.[0];
    const customer = contract?.partiesProfiles?.CUSTOMER?.[0];
    const seller = contract?.partiesProfiles?.PROVIDER?.[0];

    return (
      <>
        {contractType === EContractType.BROKERAGE_CONTRACT && (
          <>
            <h3 className={classNames(styles["g900"], styles["owner_header"])}>
              {t("lbl_transaction_parties")}
            </h3>
            <div className={styles["contract_owners"]}>
              {broker
                ? renderUserDetails(broker, "BROKER")
                : renderUserActions("", "")}
              {customer
                ? renderUserDetails(customer, "CUSTOMER")
                : renderUserActions(
                    `${t("lbl_depositor")}`,
                    `${t("lbl_no_join")}`,
                    inviteUser?.bind(null, "buyer")
                  )}
              {seller
                ? renderUserDetails(seller, "PROVIDER")
                : renderUserActions(
                    `${t("lbl_receiver")}`,
                    `${t("lbl_no_join")}`,
                    inviteUser?.bind(null, "seller")
                  )}
            </div>
          </>
        )}
        {contractType !== EContractType.BROKERAGE_CONTRACT && contract && (
          <AdditionalInformation contract={contract} />
        )}
        {(contract?.itemDetail?.images ||
          contract?.itemDetail?.attachments) && (
          <Gallery
            images={[
              ...(contract?.itemDetail?.images || []),
              ...(contract?.itemDetail?.attachments || []),
            ]}
            isBrokerContract={contractType === EContractType.BROKERAGE_CONTRACT}
          />
        )}
      </>
    );
  }, [
    contract,
    contractType,
    inviteUser,
    renderUserActions,
    renderUserDetails,
    t,
  ]);

  const toggleSuccessModal = useCallback(() => {
    setShowSuccessModal((prevState) => !prevState);
  }, []);

  const renderSnackBar = useCallback(() => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  }, [snackBarColor, snackBarOpen, snackBarTitle]);

  const navigateToContract = useCallback(async () => {
    navigate(`/contract/${contractId}`);
    setShowMenu(false);
    setShowSuccessModal(false);
  }, [contractId, navigate]);

  const navigateToParent = useCallback(async () => {
    navigate(`/contract/${contract?.parentId}`);
  }, [contract?.parentId, navigate]);

  const renderSuccess = useCallback(() => {
    return (
      <Modal
        visible={showSuccessModal}
        onCancel={toggleSuccessModal}
        size="small">
        <div className={classNames(styles.center, styles.modalContainer)}>
          <img alt="success" src="/assets/images/svg/Success.svg" />
          <h2>{t("lbl_transaction_copied_successfully")}</h2>
          <PrimaryButton onClick={navigateToContract}>
            <h3 className={styles["white"]}>{t("lbl_view_the_transaction")}</h3>
          </PrimaryButton>
        </div>
      </Modal>
    );
  }, [navigateToContract, showSuccessModal, t, toggleSuccessModal]);

  if (!contract) return <></>;
  return (
    <>
      <div className={classNames(styles["card"], styles.right_container)}>
        <div className={styles.top_section}>
          <div className={styles.top_section_right}>
            <BackButton onClick={backHandler} />
            <div className={styles["mr2"]}>
              <ContractType />
            </div>
          </div>

          <div className={styles.top_section_right}>
            {renderActions() && (
              <IconButton
                size="large"
                aria-label="show more"
                aria-controls={"ContractActions"}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                color="inherit">
                <MoreIcon />
              </IconButton>
            )}
            <ContractActions
              id="ContractActions"
              anchorEl={mobileMoreAnchorEl}
              contract={contract}
              feedBack={feedBack}
              isMenuOpen={isMenuOpen}
              handleMenuClose={handleMobileMenuClose}
            />
          </div>
        </div>
        <div className={styles.heading_wrapper}>
          <MainDetailsHeading contract={contract} />
        </div>
        <EscalationStatusBanner />
        <div className={styles.contract_details_desktop}>
          {contractDetails()}
        </div>
        <div className={styles.contract_details_mobile}>
          {displayDetails ? (
            <>{contractDetails()}</>
          ) : (
            <div
              onClick={() => setDisplayDetails(!displayDetails)}
              className={styles.hideDetails}>
              <p className={classNames("subtitle2", "primary")}>
                {t("lbl_hide_link")}
              </p>
            </div>
          )}
        </div>
      </div>
      {renderSuccess()}
      {renderSnackBar()}
      <BrokerInviteModal
        showModal={brokerModal}
        closeModal={closeBrokerModal}
        userType={inviteType as "seller" | "buyer"}
      />
      {contract?.status === ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION && (
        <div
          className={classNames(styles["card"], styles.bank_transfer_section)}>
          <div className={classNames(styles["column"], styles["stretch"])}>
            <h2>{PAYMENT_DETAILS}</h2>
            <p style={{ marginBlock: "8px" }}>
              {moment(
                contract?.transactions
                  ? contract?.transactions[0]?.createdAt
                  : new Date()
              )
                .locale("ar")
                .format(`DD MMMM YYYY - hh:mm`)}
            </p>
          </div>
          <div className={styles["mt20"]} style={{ width: "100%" }}>
            {images.length > 0 && (
              <Gallery
                isBrokerContract
                header={t("lbl_attachment_proof")}
                images={images}
                style={{ marginTop: "0.5rem" }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RightSection;
